import { useCallback, useEffect, useState } from 'react';
import { api } from '../../api/client';
import { useNotify } from '../../components/notifications';
import { useEditGate } from '../../components/confirm';
import { AppToolbar, EditGateActions } from '../../components/AppToolbar';
import { FormTitle } from '../../components/FormTitle';
import '../backlog/backlog.css';

// ─────────────────────────────────────────────────────────────────────────────
// Settings → Default Settings → Report ID Format  (legacy XAF "Report ID Format")
//
// The full segment-by-segment format builder: ReportID Format Option (Date / JobID),
// Prefix (on/off + text), Year (on/off + yy/yyyy), Month (on/off), Day (on/off),
// Sequential Number width, and a read-only composed preview. Backed by the SHARED
// singleton dbo.ReportIDFormat — a change here changes ALPACA too (CLAUDE.md §5.0).
// Service/admin only.
// ─────────────────────────────────────────────────────────────────────────────

interface Cfg {
  oid: string | null;
  formatOption: number;   // 0 Date, 1 JobID
  prefixEnabled: boolean;
  prefix: string;
  yearEnabled: boolean;
  yearFormat: number;     // 0 yy, 1 yyyy
  monthEnabled: boolean;
  dayEnabled: boolean;
  sequentialWidth: number;
  numberStart: number;
  preview: string;
  exists: boolean;
}

const MIN_W = 1;
const MAX_W = 10;

function buildPreview(o: Cfg): string {
  let s = '';
  if (o.prefixEnabled) s += (o.prefix || 'RP').trim();
  const now = new Date();
  if (o.formatOption === 1) {
    s += 'JOBID';
  } else {
    if (o.yearEnabled) s += o.yearFormat === 1 ? String(now.getFullYear()) : String(now.getFullYear()).slice(-2);
    if (o.monthEnabled) s += String(now.getMonth() + 1).padStart(2, '0');
    if (o.dayEnabled) s += String(now.getDate()).padStart(2, '0');
  }
  const w = Math.min(MAX_W, Math.max(MIN_W, o.sequentialWidth || 2));
  s += String(Math.max(0, Math.trunc(o.numberStart || 0))).padStart(w, '0');
  return s;
}

const BLANK: Cfg = {
  oid: null, formatOption: 0, prefixEnabled: true, prefix: 'RP', yearEnabled: true, yearFormat: 0,
  monthEnabled: true, dayEnabled: true, sequentialWidth: 2, numberStart: 1, preview: 'RP', exists: false,
};

export default function ReportIdFormatPage() {
  const notify = useNotify();
  const gate = useEditGate();
  const ro = gate.readOnly;

  const [cfg, setCfg] = useState<Cfg>(BLANK);
  const [form, setForm] = useState<Cfg>(BLANK);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loadMs, setLoadMs] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const t0 = performance.now();
    try {
      const res = await api.get<{ success: boolean; data: Cfg }>('/api/v1/report-id-format');
      if (res.data.success) { setCfg(res.data.data); setForm(res.data.data); }
    } catch { notify.error('Could not load Report ID Format.'); }
    finally { setLoadMs(performance.now() - t0); setLoading(false); }
  }, [notify]);

  useEffect(() => { void load(); }, [load]);

  // gate.cancel(revert) so Cancel also leaves edit mode (owner report 2026-10-12).
  const handleCancel = () => gate.cancel(() => setForm(cfg));
  const set = <K extends keyof Cfg>(k: K, v: Cfg[K]) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = () => {
    setSaving(true);
    void gate.save({
      formName: 'Report ID Format',
      successMessage: 'Report ID Format saved successfully.',
      save: async () => {
        await api.put('/api/v1/report-id-format', {
          formatOption: form.formatOption,
          prefixEnabled: form.prefixEnabled,
          prefix: form.prefix.trim(),
          yearEnabled: form.yearEnabled,
          yearFormat: form.yearFormat,
          monthEnabled: form.monthEnabled,
          dayEnabled: form.dayEnabled,
          sequentialWidth: form.sequentialWidth,
        });
      },
      reload: load,
      onError: (e: unknown) => {
        const r = e as { response?: { status?: number; data?: { error?: { message?: string } } } };
        notify.error(r?.response?.status === 403
          ? 'Administrator / service access required to change Report ID Format.'
          : r?.response?.data?.error?.message ?? 'Save failed. Please try again.');
      },
    }).finally(() => setSaving(false));
  };

  const preview = buildPreview(form);
  const isDate = form.formatOption === 0;

  // ── small building blocks ────────────────────────────────────────────────
  const YesNo = ({ on, onChange, name }: { on: boolean; onChange: (v: boolean) => void; name: string }) =>
    ro ? <span className="rif-ro">{on ? 'Yes' : 'No'}</span> : (
      <span className="rif-radios">
        <label className="rif-radio"><input type="radio" name={name} checked={on} onChange={() => onChange(true)} style={{ accentColor: 'var(--ai-orange)' }} /> Yes</label>
        <label className="rif-radio"><input type="radio" name={name} checked={!on} onChange={() => onChange(false)} style={{ accentColor: 'var(--ai-orange)' }} /> No</label>
      </span>
    );

  // 3-column row: label | Yes/No | value — the children are placed DIRECTLY as
  // grid items (not wrapped in one cell), so a row's Yes/No lands in column 2 and
  // its value in column 3, all vertically aligned across rows (legacy parity,
  // image 2). A single-value row (Format Option, Sequential Number, preview)
  // passes one child + `rif-span2` to span columns 2–3.
  const Row = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="rif-row"><div className="rif-label">{label}</div>{children}</div>
  );

  return (
    <div className="bl-page">
      <style>{`
        .rif-form { max-width: 640px; display: flex; flex-direction: column; gap: 2px; }
        .rif-row { display: grid; grid-template-columns: 190px 120px 1fr; align-items: center; column-gap: 14px; padding: 7px 0; }
        .rif-label { font-size: 12.5px; font-weight: 600; color: var(--ai-ink); }
        .rif-radios { display: inline-flex; gap: 14px; }
        .rif-radio { display: inline-flex; align-items: center; gap: 4px; font-size: 12.5px; color: var(--ai-ink); cursor: pointer; white-space: nowrap; }
        .rif-ro { font-size: 13px; font-weight: 600; color: var(--ai-ink); }
        /* single-value row (Format Option / Sequential preview) spans cols 2–3 */
        .rif-span2 { grid-column: 2 / span 2; }
        .rif-in { height: 26px; font-size: 13px; padding: 0 8px; border: 1px solid var(--ai-line); border-radius: 0; color: var(--ai-ink); background: #fff; box-sizing: border-box; }
        .rif-in:disabled { background: var(--ai-header-bg); color: var(--ai-muted); }
        .rif-preview { font-size: 14px; font-weight: 700; color: var(--ai-green); letter-spacing: 0.5px; font-family: ui-monospace, SFMono-Regular, Menlo, monospace; }
      `}</style>
      <div className="bl-top">
        <div className="dp-page-hdr">
          <div className="dp-breadcrumb">
            <span>Home</span><i className="ti ti-chevron-right" />
            <span>Settings</span><i className="ti ti-chevron-right" />
            <span>Default Settings</span><i className="ti ti-chevron-right" />
            <span className="dp-bc-active">Report ID Format</span>
          </div>
          <div className="dp-title-row">
            <FormTitle pageId="reportidformat" auditFormName="Report ID Format" loadMs={loadMs} loading={loading} />
          </div>
          <div className="dp-subtitle">
            <i className="ti ti-info-circle" style={{ fontSize: 13, verticalAlign: 'middle', marginRight: 4 }} />
            Shared with ALPACA — a change here applies to both products, and drives how new Report IDs are minted.
          </div>
        </div>
      </div>

      <div className="bl-grid-area">
        <div style={{ margin: '0 0 8px' }}>
          <AppToolbar
            left={<EditGateActions gate={gate} saving={saving} disabled={loading}
              onSave={handleSave} onCancel={handleCancel} />}
            onRefresh={() => void load()} refreshing={loading} refreshDisabled={gate.editing}
          />
        </div>

        <div className="bl-card bl-card-flex">
          <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '18px 28px 32px' }}>
            <div className="rif-form">

              <Row label="ReportID Format Option:">
                {ro ? <span className="rif-ro rif-span2">{isDate ? 'Date Format' : 'JobID Format'}</span> : (
                  <span className="rif-radios rif-span2">
                    <label className="rif-radio"><input type="radio" name="rif-opt" checked={isDate} onChange={() => set('formatOption', 0)} style={{ accentColor: 'var(--ai-orange)' }} /> Date Format</label>
                    <label className="rif-radio"><input type="radio" name="rif-opt" checked={!isDate} onChange={() => set('formatOption', 1)} style={{ accentColor: 'var(--ai-orange)' }} /> JobID Format</label>
                  </span>
                )}
              </Row>

              <Row label="Prefix:">
                <YesNo on={form.prefixEnabled} onChange={(v) => set('prefixEnabled', v)} name="rif-prefix" />
                {ro ? <span className="rif-ro">{form.prefixEnabled ? form.prefix : ''}</span> : (
                  <input type="text" className="rif-in" style={{ width: 160 }} value={form.prefix} maxLength={20}
                    disabled={!form.prefixEnabled} onChange={(e) => set('prefix', e.target.value)} />
                )}
              </Row>

              <Row label="Year:">
                <YesNo on={form.yearEnabled} onChange={(v) => set('yearEnabled', v)} name="rif-year" />
                {ro ? <span className="rif-ro">{form.yearEnabled ? (form.yearFormat === 1 ? 'yyyy' : 'yy') : ''}</span> : (
                  <span className="rif-radios">
                    <label className="rif-radio"><input type="radio" name="rif-yearfmt" checked={form.yearFormat === 0} disabled={!isDate || !form.yearEnabled} onChange={() => set('yearFormat', 0)} style={{ accentColor: 'var(--ai-orange)' }} /> yy</label>
                    <label className="rif-radio"><input type="radio" name="rif-yearfmt" checked={form.yearFormat === 1} disabled={!isDate || !form.yearEnabled} onChange={() => set('yearFormat', 1)} style={{ accentColor: 'var(--ai-orange)' }} /> yyyy</label>
                  </span>
                )}
              </Row>

              <Row label="Month:">
                <YesNo on={form.monthEnabled} onChange={(v) => set('monthEnabled', v)} name="rif-month" />
                <input type="text" className="rif-in" style={{ width: 90 }} value="mm" disabled readOnly />
              </Row>

              <Row label="Day:">
                <YesNo on={form.dayEnabled} onChange={(v) => set('dayEnabled', v)} name="rif-day" />
                <input type="text" className="rif-in" style={{ width: 90 }} value="dd" disabled readOnly />
              </Row>

              <Row label="Sequential Number:">
                {ro ? <span className="rif-ro">{form.sequentialWidth}</span> : (
                  <input type="number" className="rif-in" style={{ width: 90 }} value={form.sequentialWidth} min={MIN_W} max={MAX_W}
                    onChange={(e) => set('sequentialWidth', Math.min(MAX_W, Math.max(MIN_W, Number(e.target.value) || MIN_W)))} />
                )}
              </Row>

              <Row label="ReportID Format Display:">
                <span className="rif-preview rif-span2">{preview}</span>
              </Row>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
